import { FC, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import graphql from 'babel-plugin-relay/macro';

import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import PackageListRow from 'components/PackageListRow';
import FabDialog from 'components/FabDialog';
import PackageListAdd from 'components/PackageListAdd';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import PackageMarkupTypeFilter from 'components/PackageMarkupTypeFilter';

import { useAvoidSuspense, useSkipFirstLoad } from 'lib';
import { useTableSort } from 'components/TableSorting';
import { usePagination } from 'components/Pagination';
import { useSearch } from 'components/Search';
import { useDelete } from 'lib/graphql/useDelete';

import Query, {
  MarkupType,
  PackageListRefetchQuery,
  PackagesFilterInput,
} from '__generated__/PackageListRefetchQuery.graphql';
import { PackageList_data$key } from '__generated__/PackageList_data.graphql';

type Props = {
  data: PackageList_data$key;
};

export function makeFilter(searchParams: URLSearchParams): PackagesFilterInput {
  const f: PackagesFilterInput = {};

  const markupType = searchParams.get('markupType');

  if (markupType) {
    f.markupType = markupType as MarkupType;
  }

  return f;
}

const PackageList: FC<Props> = props => {
  const [searchParams] = useSearchParams();
  const { search } = useSearch();
  const { sort } = useTableSort();
  const { variables } = usePagination();

  const [data, { refetch, loading }] = useAvoidSuspense<
    PackageListRefetchQuery,
    PackageList_data$key
  >(
    graphql`
      fragment PackageList_data on Query
      @refetchable(queryName: "PackageListRefetchQuery") {
        packages(
          first: $first
          after: $after
          search: $search
          sort: $sort
          filters: $filters
        ) {
          __id
          edges {
            node {
              id
              ...PackageListRow_package
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  const connectionId = data.packages?.__id;

  const [deletePackage, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = data.packages ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
        sort,
        filters: makeFilter(searchParams),
      });
    }, [refetch, search, searchParams, sort, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        toolbar={
          <>
            {/* <PackageWarningsFilter sx={{ minWidth: '300px', mr: 2 }} /> */}
            <PackageMarkupTypeFilter sx={{ minWidth: '120px', mr: 2 }} />
          </>
        }
        pageInfo={pageInfo ?? null}
        icon={<BeachAccessIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Pavadinimas</TableHeadCell>

            <TableCell center>Būsena</TableCell>

            <TableHeadCell center name="price">
              Kaina
            </TableHeadCell>

            <TableHeadCell center name="createdAt">
              Sukurta
            </TableHeadCell>

            <TableHeadCell center name="updatedAt">
              Redaguota
            </TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <PackageListRow
            key={node.id}
            package={node}
            onDelete={deletePackage}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog>
          <PackageListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default PackageList;
